import React, { useEffect } from "react";
import {
  NonIdealState,
  Button,
  useHotkeys,
  HotkeyConfig,
  Colors,
} from "@blueprintjs/core";
import { delay, isNotUndefinedOrNull } from "ui-lib";
import styled from "styled-components";
import {
  Box,
  useToggle,
  Text,
  HeaderStyle,
  ErrorState,
  TitleComponent,
} from "ui-components";
import { useMutation, useQueryClient } from "react-query";
import { CommentT, TaskT } from "ui-io/task-io/task-t";
import { AssetReferenceT, ReferenceT, TodoNoteTagT } from "ui-io/task-io";
import { AssetOptions, AssetPropertiesT } from "ui-io/assets-io";
import useNewKlyvaAtom from "ui-lib/use-new-klyva-atom";
import { useSelector } from "klyva";
import * as t from "io-ts";
import { Either, isLeft, left, right } from "fp-ts/lib/Either";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
import { taskRemoveAttachmentMutation } from "ui-io/queries/tasks/task-remove-attachment-mutation";

import Comment from "./comment";
import DiscussionPanel from "./discussion-panel";
import { ValidFileTypeT } from "ui-io/assets-io/valid-file-type-io";
import AttachmentDialog from "./attachment-dialog";
import UploadAttachmentOverlay from "./upload-attachment-overlay";
import AddNewComment from "./add-new-comment";
import toasts from "../../../utils/toasts";
import useSession from "ui-io/use-session";

export type ActiveAttachmentT = {
  attachment: AssetReferenceT;
  icon: string;
  altText: string;
  getAssetUrlQuery: (options?: AssetOptions) => string;
  assetProperties: AssetPropertiesT;
  contentType: ValidFileTypeT | undefined;
};

export type AcceptedFile = {
  file: File;
  reference?: ReferenceT;
  lastModified?: number;
  name?: string;
  size?: number;
  webkitRelativePath?: string;
  type?: string;
  path?: string;
};

const ValueDecoderIO = t.array(
  t.intersection([
    t.type({
      reason: t.string,
    }),
    t.partial({
      field: t.string,
    }),
  ])
);

type ValueDecoderT = t.TypeOf<typeof ValueDecoderIO>;

const DiscussionThread = ({ preloadedTask }: { preloadedTask: TaskT }) => {
  const { session } = useSession();
  const currentUsername = session?.username;
  const [isDiscussionPanelOpen, setIsDiscussionPanelOpen] = useToggle(false);
  const discussionRef = React.useRef<HTMLDivElement>(null);
  const [attachmentOpened, setAttachmentOpened] =
    React.useState<ActiveAttachmentT>();
  const [uploadAttachmentPanel, setUploadAttachmentPanel] =
    React.useState(false);
  const queryClient = useQueryClient();
  const acceptedFilesAtom = useNewKlyvaAtom<AcceptedFile[]>(() => []);
  const referencesToAdd = useSelector(acceptedFilesAtom, (entries) =>
    entries.map(({ reference }) => reference).filter(isNotUndefinedOrNull)
  );

  const mutation = useMutation(taskRemoveAttachmentMutation, {
    onError: () => {
      toasts.error("Could not delete attachment.");
    },
    onSuccess: async () => {
      toasts.success("Attachment deleted successfully.");
      await delay(500);
      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    },
  });

  const handleToggleUpload = () => {
    setUploadAttachmentPanel((prev) => !prev);
  };

  const hotkeys: HotkeyConfig[] = React.useMemo(
    () => [
      {
        combo: "Ctrl+s",
        global: true,
        label: "Toggle Discussions Panel",
        preventDefault: true,
        onKeyDown: () => setIsDiscussionPanelOpen(),
      },
      {
        combo: "Ctrl+a",
        global: true,
        label: "Toggle Upload Panel",
        preventDefault: true,
        onKeyDown: () => handleToggleUpload(),
      },
      {
        combo: "Escape",
        global: true,
        label: "Toggle off Attachment Panel",
        preventDefault: true,
        onKeyDown: () => setUploadAttachmentPanel(false),
      },
    ],
    [setIsDiscussionPanelOpen]
  );
  useHotkeys(hotkeys);

  const handleToggleIsAttachmentOpen = (nextAttachment: ActiveAttachmentT) => {
    setAttachmentOpened(nextAttachment);
  };

  const handleCloseIsAttachmentOpen = () => {
    setAttachmentOpened(undefined);
  };

  const onDeleteAttachmentHandler = (attachmentId: string | undefined) => {
    if (!attachmentId) return;
    mutation.mutate({
      taskId: preloadedTask.id,
      attachmentId,
    });
  };

  const parseValue = (someString: string): Either<Error, ValueDecoderT> => {
    try {
      const parsedJson = JSON.parse(someString);
      const decoded = ValueDecoderIO.decode(parsedJson);
      if (isLeft(decoded)) {
        return left(new Error("Value decoding error"));
      }
      return right(decoded.right);
    } catch (error) {
      return left(new Error("Failed tao decode JSON"));
    }
  };
	useEffect(() => {
		if (discussionRef.current) {
			discussionRef.current.scrollTop = discussionRef.current.scrollHeight;
		}
	}, [preloadedTask?.comments]);

  return (
    <>
      <DiscussionHeader>
        <TitleComponent marginTop="0px" fontSize="18px">
          Discussion
        </TitleComponent>
        <Button
          icon="panel-stats"
          active={isDiscussionPanelOpen}
          intent={isDiscussionPanelOpen ? "primary" : "none"}
          onClick={setIsDiscussionPanelOpen}
        />
      </DiscussionHeader>
      <DiscussionContentWrapper>
        <MessagesWrapper >
					<ScrollSection ref={discussionRef}>

          {!preloadedTask?.comments?.length ? (
            <NonIdealState
              icon="folder-open"
              title="No comments found"
              description="Try adding the first comment"
            />
          ) : (
            preloadedTask?.comments?.map((note:CommentT, index:number) => {
              const {
								noteId,
                username,
                standardTimestampFormat,
                timeAgo,
                attachments,
                text,
                timestamp,
                tags,
								isPinned
              } = note;
              const isUsersComment = currentUsername === username;
              const Wrapper = isUsersComment ? RightWrapper : LeftWrapper;

              return (
                <>
                  <Wrapper key={index}>
                    <Comment
											noteId={noteId ?? "noID"}
											isPinned={isPinned}
                      task={preloadedTask}
                      isUsersComment={isUsersComment}
                      standardTimestampFormat={standardTimestampFormat}
                      timeAgo={timeAgo}
                      attachments={attachments}
                      username={username || ""}
                      text={text || ""}
                      handleToggleIsAttachmentOpen={
                        handleToggleIsAttachmentOpen
                      }
                      onDelete={onDeleteAttachmentHandler}
                      uploadTimeStamp={timestamp || -1}
                      tags={tags}
                    />
                  </Wrapper>
                  {note.tags?.map((tagElement:TodoNoteTagT, index:number) => {
                    if (
                      (tagElement.key !== "block_reasons" &&
                        tagElement.key !== "reject_reasons") ||
                      !tagElement.value
                    ) {
                      return null;
                    }
                    const reason = parseValue(tagElement.value);
                    if (isLeft(reason)) {
                      return <ErrorState error={reason.left} />;
                    }
                    return (
                      <Box
                        key={index}
                        style={{ position: "relative", bottom: 30 }}
                        color="GRAY4"
                        justify={currentUsername === username ? "end" : "start"}
                      >
                        <Text>
                          {tagElement.key === "block_reasons"
                            ? `Block reason: ${
                                reason.right[0]?.reason ?? "None"
                              }`
                            : reason.right.map((rejectReason, i) => (
                                <Box key={i}>
                                  {`Reject reason: ${rejectReason.field} is
                                  ${rejectReason.reason}`}
                                </Box>
                              ))}
                        </Text>
                      </Box>
                    );
                  })}
                </>
              );
            })
          )}
					</ScrollSection>

          <AddNewComment
            task={preloadedTask}
            handleToggleUpload={handleToggleUpload}
            acceptedFilesAtom={acceptedFilesAtom}
            referencesToAdd={referencesToAdd}
						/>
				</MessagesWrapper>
        {uploadAttachmentPanel && (
          <UploadAttachmentOverlay
            handleToggleUpload={handleToggleUpload}
            acceptedFilesAtom={acceptedFilesAtom}
            task={preloadedTask}
          />
        )}
        {isDiscussionPanelOpen && (
          <DiscussionPanel
            task={preloadedTask}
            handleToggleIsAttachmentOpen={handleToggleIsAttachmentOpen}
            onDelete={onDeleteAttachmentHandler}
          />
        )}
      </DiscussionContentWrapper>
      <AttachmentDialog
        attachmentOpened={attachmentOpened}
        handleCloseIsAttachmentOpen={handleCloseIsAttachmentOpen}
      />
    </>
  );
};

const DiscussionHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: sticky;
  top: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => {
    return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY2;
  }};
`;

const DiscussionContentWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
  overflow-y: auto;
  border-top-style: solid;
  border-top-width: 1px;
  border-top-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1;
  }};
`;

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
  gap: 18px;
	justify-content: end;
`;
const CommentContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const LeftWrapper = styled(CommentContainer)`
  align-self: flex-start;
  flex-direction: row-reverse;
  justify-content: flex-end;
`;

const RightWrapper = styled(CommentContainer)`
  align-self: flex-end;
  flex-direction: row;
  justify-content: end;
`;
const ScrollSection = styled.div`
	height: 100%;
	overflow:auto;
	flex:1;
	min-width: 100%;
	scrollbar-width: thin;
	padding:20px;
`
export default DiscussionThread;
